import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles, Wallet, Zap } from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  VideoCard,
} from "../shared/categoryDetailShared";

const easeOut = [0.22, 1, 0.36, 1];

function BettingSectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="relative mb-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-gradient-to-r from-orange-500 to-[#FFD221]" aria-hidden />
        <span className="text-[11px] font-black uppercase tracking-[0.28em] text-orange-600">{eyebrow}</span>
      </div>
      <h2 className="mt-3 text-2xl font-black leading-[1.15] tracking-tight text-stone-950 sm:text-3xl lg:text-[2.05rem]">
        {title}
      </h2>
      {subtitle ? <p className="mt-2 max-w-2xl text-sm font-semibold text-stone-600">{subtitle}</p> : null}
    </div>
  );
}

function CheckList({ items, color }) {
  return (
    <ul className="space-y-2.5">
      {items.map((line) => (
        <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-stone-800 sm:text-[0.9375rem]">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color }} strokeWidth={2.2} />
          <span>{line}</span>
        </li>
      ))}
    </ul>
  );
}

function SurfaceCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-orange-200/40 bg-white p-6 shadow-[0_20px_55px_-40px_rgba(234,88,12,0.22)] ring-1 ring-orange-950/[0.04] sm:p-8 ${className}`.trim()}
    >
      {children}
    </div>
  );
}

function PaymentOverviewKickoff({ data }) {
  const k = data.introKickoff;
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: easeOut }}
      className="overflow-hidden rounded-[1.75rem] border border-stone-200/80 bg-white shadow-[0_24px_80px_-40px_rgba(15,23,42,0.14)] sm:rounded-[2rem]"
    >
      <div className="grid lg:grid-cols-2 lg:items-stretch">
        <div className="relative flex flex-col justify-center overflow-hidden px-7 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
          <div
            className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full opacity-[0.055] blur-3xl"
            style={{ background: "#1c1917" }}
            aria-hidden
          />
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-white">
              {k.chipCategory}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200/80 bg-orange-50/80 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-orange-700">
              <Wallet className="h-3 w-3 shrink-0" />
              {k.chipSecondary}
            </span>
          </div>
          <h2 className="text-[1.65rem] font-black leading-[1.1] tracking-tight text-stone-950 sm:text-[2rem] lg:text-[2.1rem]">
            {k.headline}
          </h2>
          <p className="mt-4 max-w-prose text-[0.9375rem] leading-relaxed text-stone-500">{k.lead}</p>
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">Payments 101</p>
              <ul className="mt-2.5 space-y-1.5">
                {k.everydayLabels.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <Zap className="h-3 w-3 shrink-0 text-orange-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">In Ghana</p>
              <ul className="mt-2.5 space-y-1.5">
                {k.ghanaContext.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" strokeWidth={2.5} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-5 text-[0.8rem] leading-relaxed text-stone-400">{k.researchNote}</p>
          {k.sourceNote ? <p className="mt-2 text-[0.75rem] text-stone-400">{k.sourceNote}</p> : null}
          <div className="mt-5 flex flex-wrap gap-1.5">
            {k.focusTags.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-stone-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-600 shadow-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="relative flex min-h-0 flex-col border-t border-stone-100 lg:min-h-full lg:border-l lg:border-t-0">
          <div className="absolute inset-0 bg-stone-950" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
            aria-hidden
          />
          <div className="pointer-events-none absolute -right-12 -top-12 h-56 w-56 rounded-full bg-orange-500/20 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-[#FFD221]/12 blur-3xl" aria-hidden />

          <div className="relative flex flex-1 flex-col justify-center px-5 pb-2 pt-8 sm:px-8 sm:pb-3 sm:pt-10 lg:min-h-0 lg:px-8 lg:pt-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.5, ease: easeOut }}
              className="flex min-h-[min(52vh,22rem)] w-full flex-1 flex-col sm:min-h-[min(48vh,26rem)] lg:min-h-0"
            >
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 flex-1 flex-col rounded-[1.2rem] border-[5px] border-white bg-white p-1 shadow-[0_28px_70px_-25px_rgba(0,0,0,0.55)] ring-1 ring-white/15 sm:rounded-[1.35rem] sm:border-[6px] sm:p-1.5">
                  <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-stone-200">
                    <img
                      src={data.galleryImages?.[0] ?? data.heroImage}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="relative shrink-0 border-t border-white/[0.07] px-5 py-5 sm:px-8 sm:py-6 lg:px-6">
            <p className="mb-3 text-[9px] font-black uppercase tracking-[0.28em] text-white/40">At a glance</p>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {data.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-white/[0.08] bg-white/[0.06] px-2 py-3 text-center backdrop-blur-sm sm:px-3"
                >
                  <p
                    className={`text-base font-black tabular-nums sm:text-lg ${String(stat.value).includes("#") ? "text-orange-400" : "text-white"}`}
                  >
                    {stat.value}
                  </p>
                  <p className="mt-0.5 text-[8px] font-bold uppercase leading-tight tracking-wide text-white/40 sm:text-[9px]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] font-medium text-white/28">
              Educational — not financial advice; use licensed operators only.
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function QnaBlock({ block, color }) {
  const header = (
    <div className="flex flex-wrap items-center gap-4 border-b border-orange-100/90 bg-gradient-to-r from-orange-500/10 via-white to-transparent px-6 py-5 sm:px-8">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-900 text-lg font-black text-[#FFD221] shadow-lg">
        ?
      </span>
      <h3 className="flex-1 text-lg font-black leading-snug text-stone-950 sm:text-xl">{block.question}</h3>
    </div>
  );

  const body = (() => {
    switch (block.id) {
      case "fastest-payout":
        return (
          <>
            <p className="text-xs font-black uppercase text-stone-600">{block.dependsTitle}</p>
            <div className="mt-2">
              <CheckList items={block.depends} color={color} />
            </div>
            <p className="mt-8 text-sm font-black text-stone-950">✔ {block.preferTitle}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {block.prefer.map((p) => (
                <div key={p.title} className="rounded-xl border border-orange-100 bg-orange-50/40 p-4">
                  <p className="text-xs font-black uppercase text-orange-800">{p.title}</p>
                  <p className="mt-2 text-sm font-medium text-stone-700">{p.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4">
              <p className="text-xs font-black uppercase text-amber-900">{block.delayTitle}</p>
              {block.delayReasons.map((d) => (
                <p key={d} className="mt-2 text-sm font-semibold text-amber-950">
                  ⚠ {d}
                </p>
              ))}
            </div>
          </>
        );
      case "withdrawal-delayed":
        return (
          <>
            <p className="text-xs font-black uppercase text-stone-600">{block.reasonsTitle}</p>
            <ul className="mt-3 space-y-3">
              {block.reasons.map((r) => (
                <li key={r.title} className="rounded-xl border border-stone-100 bg-stone-50/90 px-4 py-3">
                  <p className="text-sm font-black text-stone-950">✔ {r.title}</p>
                  <p className="mt-1 text-sm text-stone-600">{r.body}</p>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4">
              <p className="text-xs font-black uppercase text-emerald-900">{block.importantTitle}</p>
              <p className="mt-2 text-sm font-semibold text-emerald-950">{block.important}</p>
            </div>
          </>
        );
      case "how-verify":
        return (
          <>
            <p className="text-xs font-black uppercase text-orange-600">{block.processTitle}</p>
            <p className="mt-2 text-sm font-semibold text-stone-700">Most betting platforms request:</p>
            <div className="mt-3">
              <CheckList items={block.documents} color={color} />
            </div>
            <p className="mt-4 text-xs font-black uppercase text-stone-600">Sometimes:</p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {block.sometimes.map((s) => (
                <li key={s} className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-bold text-stone-800">
                  {s}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm font-black text-stone-950">✔ {block.whyTitle}</p>
            <div className="mt-3">
              <CheckList items={block.why} color="#059669" />
            </div>
          </>
        );
      case "withdrawal-limits":
        return (
          <>
            <p className="text-sm font-semibold text-stone-700">{block.intro}</p>
            <p className="mt-6 text-xs font-black uppercase text-stone-600">{block.examplesTitle}</p>
            <dl className="mt-3 space-y-2 rounded-xl bg-stone-900 p-4 text-sm text-white">
              <div className="flex justify-between gap-4">
                <dt className="text-white/60">Minimum (examples)</dt>
                <dd className="font-black text-right">{block.minimum}</dd>
              </div>
              <div className="flex justify-between gap-4 border-t border-white/10 pt-2">
                <dt className="text-white/60">Maximum</dt>
                <dd className="font-black text-right">{block.maximum}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm font-bold text-amber-900">⚠ {block.important}</p>
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-orange-200/50 bg-stone-50/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
      {header}
      <div className="space-y-4 p-6 sm:p-8">{body}</div>
    </div>
  );
}

export const PaymentMethodsOverviewTab = ({ data, color }) => (
  <div className="relative space-y-14">
    <PaymentOverviewKickoff data={data} />

    <section>
      <BettingSectionHead eyebrow="Trust" title={data.whyImportant.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-800">{data.whyImportant.hook}</p>
        <p className="mt-6 text-xs font-black uppercase text-orange-600">{data.whyImportant.careTitle}</p>
        <div className="mt-3">
          <CheckList items={data.whyImportant.care} color={color} />
        </div>
        <p className="mt-6 text-sm text-stone-600">{data.whyImportant.context}</p>
        <p className="mt-6 text-xs font-black uppercase text-stone-600">{data.whyImportant.useTitle}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.whyImportant.use.map((u) => (
            <span key={u} className="rounded-full bg-stone-900 px-3 py-1.5 text-xs font-black text-white">
              {u}
            </span>
          ))}
        </div>
        <p className="mt-6 text-sm text-stone-600">{data.whyImportant.closing}</p>
        <p className="text-xs text-stone-400">{data.whyImportant.sourceNote}</p>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Flow" title={data.simpleFlow.headline} subtitle={data.simpleFlow.sub} />
      <div className="space-y-3">
        {data.simpleFlow.steps.map((step, i) => (
          <div key={step.title} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-black text-orange-600">
              ✔ {i + 1}. {step.title}
            </p>
            {step.bullets?.length ? (
              <ul className="mt-2 text-sm font-semibold text-stone-700">
                {step.bullets.map((b) => (
                  <li key={b}>· {b}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="MoMo" title={data.momoDominates.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.momoDominates.lead}</p>
        <div className="mt-4">
          <CheckList items={data.momoDominates.pros} color={color} />
        </div>
        <p className="mt-8 text-xs font-black uppercase text-stone-600">{data.momoDominates.preferTitle}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.momoDominates.prefer.map((p) => (
            <span key={p} className="rounded-lg border border-orange-200 bg-orange-50/60 px-3 py-1.5 text-xs font-black text-orange-900">
              {p}
            </span>
          ))}
        </div>
        <p className="mt-4 text-sm text-stone-600">{data.momoDominates.closing}</p>
      </SurfaceCard>
    </section>

    <section className="space-y-8">
      <BettingSectionHead eyebrow="FAQ" title="COMMON QUESTIONS GHANAIANS ASK" />
      {data.commonQna.map((block) => (
        <QnaBlock key={block.id} block={block} color={color} />
      ))}
    </section>

    <section>
      <BettingSectionHead eyebrow="Compliance" title={data.kyc.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.kyc.lead}</p>
        <div className="mt-4">
          <CheckList items={data.kyc.purposes} color={color} />
        </div>
        <p className="mt-8 text-sm font-black text-stone-950">{data.kyc.requirementsTitle}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {data.kyc.blocks.map((b) => (
            <div key={b.title} className="rounded-xl border border-stone-100 bg-stone-50/90 p-4">
              <p className="text-xs font-black uppercase text-orange-700">📘 {b.title}</p>
              {"items" in b && b.items ? (
                <ul className="mt-2 text-sm font-medium text-stone-800">
                  {b.items.map((x) => (
                    <li key={x}>· {x}</li>
                  ))}
                </ul>
              ) : null}
              {"body" in b && b.body ? <p className="mt-2 text-sm text-stone-600">{b.body}</p> : null}
            </div>
          ))}
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Problems" title={data.paymentProblems.headline} />
      <SurfaceCard className="!border-amber-200/50">
        <p className="text-sm font-black text-amber-950">{data.paymentProblems.failedTitle}</p>
        <p className="mt-2 text-sm text-stone-700">{data.paymentProblems.failedNote}</p>
        <p className="mt-6 text-xs font-black uppercase text-stone-600">{data.paymentProblems.causesTitle}</p>
        <div className="mt-2">
          <CheckList items={data.paymentProblems.causes} color={color} />
        </div>
        <p className="mt-8 text-sm font-black text-stone-950">📌 {data.paymentProblems.doTitle}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {data.paymentProblems.actions.map((a) => (
            <div key={a.title} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-black text-stone-950">{a.title}</p>
              <p className="mt-2 text-sm text-stone-600">{a.body}</p>
            </div>
          ))}
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Withdraw" title={data.withdrawSafely.headline} />
      <div className="grid gap-4 sm:grid-cols-2">
        {data.withdrawSafely.practices.map((p) => (
          <SurfaceCard key={p.title} className="!py-5">
            <p className="text-sm font-black text-stone-950">✔ {p.title}</p>
            <p className="mt-2 text-sm text-stone-600">{p.body}</p>
          </SurfaceCard>
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Timing" title={data.withdrawalTiming.headline} />
      <SurfaceCard>
        <p className="text-xs font-black uppercase text-stone-600">{data.withdrawalTiming.varyTitle}</p>
        <div className="mt-2">
          <CheckList items={data.withdrawalTiming.vary} color={color} />
        </div>
        <p className="mt-8 text-sm font-black text-stone-950">{data.withdrawalTiming.expectationsTitle}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-stone-800">
          <li>📱 {data.withdrawalTiming.momo}</li>
          <li>🏦 {data.withdrawalTiming.bank}</li>
        </ul>
        <p className="mt-6 text-sm font-bold text-amber-900">⚠ {data.withdrawalTiming.important}</p>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Bonuses" title={data.bonusWithdrawal.headline} />
      <SurfaceCard>
        <p className="text-sm text-stone-700">{data.bonusWithdrawal.confuse}</p>
        <p className="mt-6 text-xs font-black uppercase text-red-800">{data.bonusWithdrawal.patternTitle}</p>
        <p className="text-sm font-semibold text-red-950">{data.bonusWithdrawal.pattern}</p>
        <p className="mt-6 text-sm font-black text-stone-950">{data.bonusWithdrawal.termsTitle}</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {data.bonusWithdrawal.terms.map((t) => (
            <div key={t.title} className="rounded-xl border border-stone-100 bg-stone-50 p-4">
              <p className="text-xs font-black text-orange-800">📘 {t.title}</p>
              <p className="mt-2 text-sm text-stone-700">{t.body}</p>
            </div>
          ))}
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Security" title={data.security.headline} />
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-6">
          <p className="text-xs font-black uppercase text-red-900">{data.security.risksTitle}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-red-950">
            {data.security.risks.map((r) => (
              <li key={r}>🚩 {r}</li>
            ))}
          </ul>
        </div>
        <SurfaceCard>
          <p className="text-xs font-black uppercase text-emerald-900">{data.security.tipsTitle}</p>
          <div className="mt-3">
            <CheckList items={data.security.tips} color="#059669" />
          </div>
        </SurfaceCard>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Trust" title={data.trustedPlatforms.headline} />
      <SurfaceCard>
        <p className="text-xs font-black uppercase text-emerald-800">{data.trustedPlatforms.usuallyTitle}</p>
        <div className="mt-3">
          <CheckList items={data.trustedPlatforms.signals} color="#059669" />
        </div>
        <p className="mt-6 text-sm text-stone-600">{data.trustedPlatforms.regulatorNote}</p>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Discipline" title={data.responsiblePayments.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.responsiblePayments.lead}</p>
        <p className="mt-6 text-xs font-black uppercase text-red-800">{data.responsiblePayments.warningsTitle}</p>
        <ul className="mt-2 space-y-1 text-sm font-bold text-red-900">
          {data.responsiblePayments.warnings.map((w) => (
            <li key={w}>🚩 {w}</li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase text-emerald-800">{data.responsiblePayments.tipsTitle}</p>
        <div className="mt-2">
          <CheckList items={data.responsiblePayments.tips} color="#059669" />
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Support" title={data.accountSuspended.headline} />
      <SurfaceCard>
        <p className="text-xs font-black uppercase text-stone-600">{data.accountSuspended.becauseTitle}</p>
        <div className="mt-2">
          <CheckList items={data.accountSuspended.reasons} color={color} />
        </div>
        <p className="mt-8 text-sm font-black text-stone-950">{data.accountSuspended.approachTitle}</p>
        <ol className="mt-4 space-y-3 border-l-2 border-orange-200 pl-4">
          {data.accountSuspended.steps.map((s, i) => (
            <li key={s.title} className="text-sm text-stone-800">
              <span className="font-black text-orange-600">{i + 1}. {s.title}: </span>
              {s.body}
            </li>
          ))}
        </ol>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Scams" title={data.scams.headline} />
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border-2 border-red-200 bg-red-50/50 p-6">
          <p className="text-xs font-black uppercase text-red-900">{data.scams.scamsTitle}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-red-950">
            {data.scams.scams.map((s) => (
              <li key={s}>🚩 {s}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-stone-900/10 bg-stone-900 p-6 text-white">
          <p className="text-xs font-black uppercase text-[#FFD221]">{data.scams.neverTitle}</p>
          <ul className="mt-3 space-y-2 text-sm font-bold">
            {data.scams.never.map((n) => (
              <li key={n}>❌ {n}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Editorial" title={data.contentIdeas.headline} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.contentIdeas.groups.map((g) => (
          <div key={g.title} className="rounded-2xl border border-stone-200 bg-stone-50/80 p-5">
            <p className="text-sm font-black text-stone-950">{g.title}</p>
            <ul className="mt-3 space-y-1 text-sm font-semibold text-stone-700">
              {g.bullets.map((b) => (
                <li key={b}>· {b}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section className="overflow-hidden rounded-[1.75rem] border border-orange-400/30 bg-gradient-to-br from-[#1a1512] via-stone-900 to-orange-950 p-8 text-white shadow-2xl sm:p-10">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="h-8 w-8 text-[#FFD221]" />
        <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
          Infoboxx funnel
        </span>
      </div>
      <h3 className="mt-5 text-2xl font-black sm:text-3xl">{data.funnel.headline}</h3>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          {[
            { k: "Hook", v: data.funnel.hook },
            { k: "Education", v: data.funnel.education },
            { k: "Trust", v: data.funnel.trust },
          ].map(({ k, v }) => (
            <div key={k} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-200">{k}</p>
              <p className="mt-2 text-sm font-semibold text-white/92">{v}</p>
            </div>
          ))}
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">Conversion</p>
          <ul className="mt-3 space-y-2 text-sm font-bold text-white/90">
            {data.funnel.conversion.map((c) => (
              <li key={c} className="flex gap-2 rounded-xl border border-white/10 bg-black/25 px-3 py-2">
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="rounded-[1.75rem] border-2 border-orange-300/60 bg-gradient-to-br from-[#FFD221]/20 via-white to-orange-50/40 p-8 sm:p-10">
      <BettingSectionHead eyebrow="CTAs" title={data.strongCta.headline} />
      <ul className="mt-4 space-y-3">
        {data.strongCta.items.map((item) => (
          <li key={item} className="flex gap-3 text-sm font-black text-stone-900 sm:text-[0.9375rem]">
            <span className="text-orange-600">👉</span>
            {item}
          </li>
        ))}
      </ul>
    </section>

    <section>
      <BettingSectionHead eyebrow="Summary" title={data.takeaway.headline} />
      <div className="rounded-2xl border border-stone-200 bg-gradient-to-b from-white to-stone-50/50 p-6 sm:p-8">
        {data.takeaway.paragraphs.map((p) => (
          <p key={p} className="mb-4 last:mb-0 text-sm font-semibold leading-relaxed text-stone-700">
            {p}
          </p>
        ))}
        <p className="mb-4 text-xs font-black uppercase text-stone-600">The safest approach is to:</p>
        <div className="mb-4">
          <CheckList items={data.takeaway.bullets} color={color} />
        </div>
        <div className="rounded-xl border-l-4 border-orange-500 bg-orange-50/50 p-4 text-sm font-semibold text-stone-800">
          👉 {data.takeaway.footnote}
        </div>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Quick answers" title="FAQs" />
      <div className="rounded-2xl border border-orange-100/90 bg-white p-4 shadow-md sm:p-6">
        <div className="space-y-2 divide-y divide-orange-100/80">
          {data.faqs.map((faq) => (
            <div key={faq.q} className="pt-2 first:pt-0">
              <FAQItem q={faq.q} a={faq.a} color={color} />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export const PaymentMethodsArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-12">
    <section>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <BettingSectionHead eyebrow="Library" title="Articles & guides" subtitle={data.funnelTagline} />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">
          {data.articles.length} guides
        </span>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>
    <section className="overflow-hidden rounded-[1.5rem] border border-orange-200/70 bg-gradient-to-br from-stone-900 to-orange-950 p-8 text-white sm:p-10">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em] text-[#FFD221]">{data.editorialPlaybook.leadCtasHeading}</p>
          <h3 className="text-xl font-black sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm text-white/85">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-[#FFD221] px-8 py-4 text-sm font-black text-black shadow-lg transition hover:brightness-105"
        >
          Register free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const PaymentMethodsMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;
  return (
    <div className="min-w-0 space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <BettingSectionHead
          eyebrow="Watch & listen"
          title="Videos & podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
        />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">{total} featured</span>
      </div>
      {videos.length > 0 ? (
        <section className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md sm:p-6">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Betting payment education videos"
            items={videos}
            header={<h3 className="text-lg font-black text-stone-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}
      {podcasts.length > 0 ? (
        <section className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md sm:p-6">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Betting payment podcasts"
            items={podcasts}
            header={<h3 className="text-lg font-black text-stone-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
        </section>
      ) : null}
    </div>
  );
};
